import * as React from "react";
import classNames from "classnames";
import { backgroundStyleFromNode } from "../../../helpers/Doc/changes";
import createAiAssistantChatSubscription from "../../../channels/ai_assistant_chat_channel";

import AqButton from "../../Aq/Button";
import GenLoadingSpinner from '../../Gen/LoadingSpinner/component';
import DocAiAssistantThreadFeedbackModal from "../Modals/AiAssistantThreadFeedback";
import { getNewThread } from "../../../aiAssistant";
import { handleActions } from "../../../helpers/Doc/handleActions";
import GenModal from '../../Gen/Modal/component';

const MINIMUM_MESSAGE_COUNT = 5
const FEEDBACK_TIMEOUT_MS = 120000
const STREAMING_DONE_MS = 1500

async function getMessagesFromDus(userId: number, documentId: number, chat_uid: string) {
  const response = await fetch(
    `/ai_assistant/messages/get_messages_from_dus?user_id=${userId}&document_id=${documentId}&chat_uid=${chat_uid}`, 
    {
      method: "GET",
      headers: {
        "Accept": "application/json",
        "X-CSRF-Token": (document.querySelector("meta[name='csrf-token']") as HTMLMetaElement).content
      }
    }
  );

  if (!response.ok) {
    const text = await response.text();
    console.error("Fetch error:", text);
    throw new Error("Failed to fetch messages: " + text);
  }

  return response.json();
}

function convertSimpleMarkdown(md: string) {
  // Convert headings
  let html = md.replace(/^### (.*)$/gm, '<h4>$1</h4>');

  // Convert bold
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

  // Convert line breaks
  html = html.replace(/\n/g, '<br>');

  return html;
}

interface AiAssistantFeedbackProps extends DocAiAssistantFeedbackType<InlineNode>, BlockNodeAll {}
function AiAssistantFeedback(props: AiAssistantFeedbackProps) {
  const {
    uid,
    userContext,
    documentContext,
    displayContext,
    editingContext,
    assistant_id,
    chat_uid,
    feedback = [],
    onContinue,
    threadId,
    inactiveBool,
    currentVersion,
  } = props;

  const [response, setResponse] = React.useState<string>();
  const [openFeedbackModal, setOpenFeedbackModal] = React.useState(false);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = React.useState(false);

  const subscriptionRef = React.useRef<ReturnType<typeof createAiAssistantChatSubscription>>();
  const hasFeedback = React.useMemo(() => Array.isArray(feedback) && feedback[0]?.content, [feedback]);
  const threadCreationPromiseRef = React.useRef<Promise<any> | null>(null);
  const messageSubmittedRef = React.useRef<boolean>(false);
  const latestContentRef = React.useRef<string>("");
  const responseTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const completionTimerRef = React.useRef<NodeJS.Timeout | null>(null);

  const [errorModalOpen, setErrorModalOpen] = React.useState(false);

  const clearTimers = React.useCallback(() => {
    if (responseTimeoutRef.current) {
      clearTimeout(responseTimeoutRef.current);
      responseTimeoutRef.current = null;
    }
    if (completionTimerRef.current) {
      clearTimeout(completionTimerRef.current);
      completionTimerRef.current = null;
    }
  }, []);

  const showError = React.useCallback(() => {
    clearTimers();
    setErrorModalOpen(true);
  }, [clearTimers]);

  const showAiFallbackCards = React.useCallback(() => {
    userContext.addState({
      [currentVersion.rootUid]: {
        error: "aiAssistantFeedbackError",
        aiFallbackCards: true,
      },
    });
  }, [userContext, currentVersion.rootUid]);

  React.useEffect(() => {
    if (hasFeedback) {
      setResponse(feedback[0].content);
      return;
    }

    setResponse(
      "If you do not see feedback below, close this card by clicking on the minus sign in the upper left-hand corner of the card, and make sure you asked at least five questions above. After submitting your questions, click \"I don't have any more questions,\" rate the experience, and reopen this card."
    );

    if (editingContext.isEditing) return;

    async function fetchAndSubscribe() {
      try {
        const dusData = await getMessagesFromDus(userContext.id, documentContext.id, chat_uid);
        const userMessages = Array.isArray(dusData?.messages)
          ? dusData.messages.filter((msg) => msg.role === "user")
          : [];
        if (userMessages.length < MINIMUM_MESSAGE_COUNT) return;

        const userMessage = {
          content: (dusData?.messages ?? [])
            .map((msg: { role: string; content: string }) => `${msg.role}: ${msg.content}`)
            .join("\n\n"),
          role: "user" as const,
        };
        if (!userMessage.content) return;

        let threadIdResolved = threadId;
        if (!threadIdResolved) {
          if (threadCreationPromiseRef.current) {
            threadIdResolved = (await threadCreationPromiseRef.current).id;
          } else {
            threadCreationPromiseRef.current = getNewThread(assistant_id, userContext.id, "vpe_feedback")
              .then((thread) => {
                userContext.addState({ [uid]: { threadId: thread.id } });
                return thread;
              })
              .finally(() => { threadCreationPromiseRef.current = null; });
            threadIdResolved = (await threadCreationPromiseRef.current).id;
          }
        }

        if (!messageSubmittedRef.current) {
            // Unsubscribe from existing subscription if it exists
            if (subscriptionRef.current?.unsubscribe) {
              subscriptionRef.current.unsubscribe();
              subscriptionRef.current = null;
            }

            messageSubmittedRef.current = true;

            const onReceived = (data: AiAssistantMessage & { status?: string }) => {
              if (data.role !== "assistant") return;

              if (data.status === "error") {
                showError();
                return;
              }

              if (responseTimeoutRef.current) {
                clearTimeout(responseTimeoutRef.current);
                responseTimeoutRef.current = null;
              }

              setResponse(data.content);
              latestContentRef.current = data.content ?? "";
              userContext.addState({ [uid]: { feedback: [data] } });

              if (completionTimerRef.current) {
                clearTimeout(completionTimerRef.current);
              }
              const waitMs = data.content?.trim() ? STREAMING_DONE_MS : FEEDBACK_TIMEOUT_MS;
              completionTimerRef.current = setTimeout(() => {
                const content = latestContentRef.current;
                const isBad =
                  !content.trim() || content.includes("We had trouble generating a response");
                if (isBad) showError();
                completionTimerRef.current = null;
              }, waitMs);
            };

            // Track if chat has been submitted to prevent duplicate submissions on reconnect
            let chatSubmitted = false;

            subscriptionRef.current = createAiAssistantChatSubscription({
              threadId: `${threadIdResolved}`,
              onReceived,
              onConnected: () => {
                // Prevent duplicate submissions if onConnected fires multiple times (e.g., reconnect)
                if (chatSubmitted) return;
                chatSubmitted = true;

                if (subscriptionRef.current.isConnected()) {
                  subscriptionRef.current.submitChat(userMessage);
                } else {
                  subscriptionRef.current.reconnect(() => {
                    subscriptionRef.current.submitChat(userMessage);
                  });
                }

                if (responseTimeoutRef.current) clearTimeout(responseTimeoutRef.current);
                responseTimeoutRef.current = setTimeout(showError, FEEDBACK_TIMEOUT_MS);
              },
              onDisconnected: () => {},
              onRejected: () => {},
            });
        }
      } catch (error) {
        // Reset thread creation ref on failure to allow retry
        threadCreationPromiseRef.current = null;

        setResponse("Something went wrong");
        return;
      }
    }

    fetchAndSubscribe();

    return () => {
      subscriptionRef.current?.unsubscribe?.();
      clearTimers();
    };
  }, [clearTimers, showError]);

  return (
    <div
      id={props.uid}
      data-is-node
      data-uid={props.uid}
      className={classNames("ai-assistant-feedback", props.uid)}
      style={{...backgroundStyleFromNode(props), marginBottom: "10px"}}
    >
      {displayContext.displayMode !== "student" && (
        <div style={{
          padding: "15px",
          marginBottom: "15px",
          backgroundColor: "#f0f8ff",
          border: "1px solid #99D4EB",
          borderRadius: "4px"
        }}
        contentEditable={false}
        >
          <strong>Note to Educators:</strong> The feedback below is generated after at least five questions have been submitted to the virtual patient in the card above and shows the structure of feedback that students see. The exact feedback will vary, depending on the questions asked.
        </div>
      )}

      <div className="ai-response-body">
        {editingContext.isEditing ? (
          <div style={{
            padding: "20px",
            color: "#666",
            fontStyle: "italic",
            backgroundColor: "#f9f9f9",
            border: "1px solid #e0e0e0",
            borderRadius: 8,
          }}>
            Feedback will be here in view mode
          </div>
        ) : (
          !response ? <GenLoadingSpinner /> : <div dangerouslySetInnerHTML={{ __html: convertSimpleMarkdown(response) }} />
        )}
      </div>

      <br/>
      <div>
        {!inactiveBool && (
          <AqButton
            onClick={() => {
              if (!isSubmittingFeedback) {
                setOpenFeedbackModal(true);
              }
            }}
            disabled={!hasFeedback || isSubmittingFeedback}
          >
            CONTINUE
          </AqButton>
        )}
      </div>

      {openFeedbackModal && (
        <DocAiAssistantThreadFeedbackModal
          threadId={threadId}
          experienceQuestion="My reaction to this feedback on my VPE chat is best expressed as:"
          responseOptions={[
            "All good, feedback was clear and helpful",
            "Feedback was unclear",
            "Feedback was inaccurate",
            "Slow, unresponsive, or glitchy",
            "Other, please specify below",
          ]}
          onContinue={() => {
            setOpenFeedbackModal(false);
            if (onContinue) {
              setIsSubmittingFeedback(true);
              handleActions(onContinue, props);
            }
          }}
          onCancel={() => setOpenFeedbackModal(false)}
        />
      )}

      <GenModal open={errorModalOpen}>
        <div className="gen-modal-panel gen-modal-panel-small">
          <h2>AN ERROR HAS OCCURRED</h2>
          <p>
            Oops! This feature is experiencing difficulty. Sorry for the hassle. You can still complete the case without
            the Virtual Patient Feedback. Your progress has been saved and you will be directed to the card immediately
            preceding the VPE. A record of this error has been logged.
          </p>
          <p>
            You can either proceed through the traditional format of this case without the feedback or return later when
            the issue may be resolved.
          </p>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <button
              className="aq-button-2"
              onClick={() => {
                setErrorModalOpen(false);
                showAiFallbackCards();
              }}
            >
              CONTINUE
            </button>
          </div>
        </div>
      </GenModal>
    </div>
  );
}

export default React.memo(AiAssistantFeedback);
